/**
 * utils/requestDebouncer.js
 * Utilidad para evitar requests duplicados simultáneos
 * 
 * ⚡ OPTIMIZACIÓN: Reduce lecturas de Firebase evitando llamadas redundantes
 */

class RequestDebouncer {
    constructor() {
        // Map de requests en progreso: key -> Promise
        this.pendingRequests = new Map();
        
        // Map de últimos resultados en caché: key -> {data, timestamp}
        this.cache = new Map();
        
        // Duración del caché (5 segundos por defecto)
        this.cacheDuration = 5000;
    }

    /**
     * Ejecuta un request con debouncing
     * Si ya hay un request idéntico en progreso, retorna el mismo Promise
     * Si hay un resultado en caché reciente, lo retorna inmediatamente
     * 
     * @param {string} key - Identificador único del request
     * @param {Function} requestFn - Función que ejecuta el request
     * @param {Object} options - Opciones { useCache: boolean, cacheDuration: number }
     * @returns {Promise} - Resultado del request
     */
    async execute(key, requestFn, options = {}) {
        const { useCache = true, cacheDuration = this.cacheDuration } = options;

        // 1. Verificar caché si está habilitado
        if (useCache) {
            const cached = this.cache.get(key);
            if (cached && (Date.now() - cached.timestamp) < cacheDuration) {
                // console.log(`⚡ [Debouncer] Cache HIT: ${key}`);
                return cached.data;
            }
        }

        // 2. Verificar si ya hay un request en progreso
        if (this.pendingRequests.has(key)) {
            // console.log(`⚡ [Debouncer] Request duplicado evitado: ${key}`);
            return this.pendingRequests.get(key);
        }

        // 3. Ejecutar nuevo request
        // console.log(`⚡ [Debouncer] Ejecutando request: ${key}`);
        const requestPromise = requestFn()
            .then(result => {
                // Guardar en caché
                if (useCache) {
                    this.cache.set(key, {
                        data: result,
                        timestamp: Date.now()
                    });
                }
                
                // Remover de pendientes
                this.pendingRequests.delete(key);
                
                return result;
            })
            .catch(error => {
                // Remover de pendientes en caso de error
                this.pendingRequests.delete(key);
                throw error;
            });

        // Guardar en pendientes
        this.pendingRequests.set(key, requestPromise);

        return requestPromise;
    }

    /**
     * Limpia el caché manualmente
     * @param {string} key - Si se especifica, limpia solo esa key. Si no, limpia todo
     */
    clearCache(key = null) {
        if (key) {
            this.cache.delete(key);
            // console.log(`⚡ [Debouncer] Cache limpiado: ${key}`);
        } else {
            this.cache.clear();
            // console.log(`⚡ [Debouncer] Todo el cache limpiado`);
        }
    }

    /**
     * Limpia requests pendientes
     */
    clearPending() {
        this.pendingRequests.clear();
        // console.log(`⚡ [Debouncer] Requests pendientes limpiados`);
    }

    /**
     * Obtiene estadísticas del debouncer
     */
    getStats() {
        return {
            pendingRequests: this.pendingRequests.size,
            cachedItems: this.cache.size,
            cacheKeys: Array.from(this.cache.keys())
        };
    }
}

// Instancia singleton
const debouncer = new RequestDebouncer();

export default debouncer;

/**
 * Hook de React para usar el debouncer
 */
export const useDebouncedRequest = () => {
    return {
        execute: debouncer.execute.bind(debouncer),
        clearCache: debouncer.clearCache.bind(debouncer),
        getStats: debouncer.getStats.bind(debouncer)
    };
};
